import { Box, Backdrop, Button, Grid, styled, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAddKegiatanSosial } from "../../../../api/effects/mutations/KegiatanSosial/addKegiatanSosial";
import { useEditKegiatanSosial } from "../../../../api/effects/mutations/KegiatanSosial/editKegiatanSosial";
import { useFetchKegiatanSosial } from "../../../../api/effects/mutations/KegiatanSosial/useFetchKegiatanSosial";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { formatDate, convertDate, toIsoStringDate } from "../../../../utils/dateConvert";
import InputDateForm from "../../../../components/InputDateForm";
import { InputTextFieldForm } from "../../../../components/InputTextFieldForm";



const SquareGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FFF",
  height: "auto",
  width: "90%",
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "1%",
  [theme.breakpoints.down("sm")]: {
    padding: "10px",
  },
  margin: "1%",
}));

const FullButton = styled(Button)(({ theme }) => ({
  width: "70%",
}));

const ScrollGrid = styled(Grid)(({ theme }) => ({
  overflow: "auto",
  maxHeight: "90vh",
}));

type FormValues = {
  organisasi: string;
  kegiatan: string;
  jabatan: string;
  tahun: string;
};

type Props = {
  open: boolean;
  handleClose: () => void;
  ID?: number;
};


const schema = z.object({
  organisasi: z.string().min(1, "Organisasi field is required"),
  kegiatan: z.string().min(1, "Kegiatan  field is required"),
  jabatan: z.string().min(1, "Jabatan field is required"),
  //  Tahun Kegiatan
  tahun: z.string({ required_error: "Tanggal Kegiatan field is required" })
    .min(1, "Tanggal Kegiatan field is required").refine(val => {
      const date = new Date(val);
      return !isNaN(date.getTime()); // Check if the date is valid
    }, { message: "Invalid date format" })
    .refine(val => {
      const date = new Date(val);
      const minDate = new Date(1900, 0, 1);
      const maxDate = new Date();
      return date >= minDate && date <= maxDate; // Check if the date is within the valid range
    }, { message: "Date must be between January 1, 1900 and today" }),
})

export default function KegiatanSosialModal({
  ID = -1,
  open,
  handleClose,
}: Props) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormValues>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      organisasi: "",
      kegiatan: "",
      jabatan: "",
      tahun: "",
    },
  });
  const { mutateAsync: GetKegiatanSosialData } = useFetchKegiatanSosial({
    onSuccess(data) {
      setValue("organisasi", data.ORGINASASI)
      setValue("kegiatan", data.KEGIATAN)
      setValue("jabatan", data.JABATAN)
      setValue("tahun", formatDate(data.TAHUN))
      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  useEffect(() => {
    if (ID > 0) {
      GetKegiatanSosialData(ID);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);


  const { mutateAsync: AddKegiatanSosial, isLoading: isAdding } = useAddKegiatanSosial({
    onSuccess() {
      handleModalClose();
    },
    onError() {
      console.log("Error");
    },
  });

  const { mutateAsync: EditKegiatanSosial, isLoading: isEditing } = useEditKegiatanSosial({
    onSuccess() {
      handleModalClose();
    },
    onError() {
      console.log("Error");
    },
  });

  const handleModalClose = () => {
    reset();
    handleClose();
  };
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const payload = {
      KEGIATAN: data.kegiatan,
      ORGINASASI: data.organisasi,
      JABATAN: data.jabatan,
      TAHUN: toIsoStringDate(data.tahun),
    };
    try {
      if (ID > 0) {
        await EditKegiatanSosial({
          KEGIATAN_SOSIAL_ID: ID,
          ...payload,
        });
      } else {
        await AddKegiatanSosial(payload);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <Box component={"form"} onSubmit={handleSubmit(onSubmit)} noValidate>


        <SquareGrid container>
          <ScrollGrid container>
            <Grid container justifyContent="flex-start" paddingLeft={"10.5%"} item xs={6}
              paddingBottom={
                {
                  xs: 5,
                  sm: 5,
                  md: 5,
                  lg: 5,
                  xl: 5
                }}>
              <Typography variant="subtitle1" color="black">
                {ID === -1
                  ? "Tambah Pengalaman Kegiatan Sosial"
                  : "Ubah Pengalaman Kegiatan Sosial"}
              </Typography>
            </Grid>
            <Grid container justifyContent="flex-end" item xs={6}>
              <Button onClick={handleClose}>Close</Button>
            </Grid>

            {/* organisasi - Text Field */}
            <InputTextFieldForm
              name="organisasi"
              control={control}
              label="Organisasi"
              error={errors.organisasi}
            />
            {/* kegiatan - Text Field */}
            <InputTextFieldForm
              name="kegiatan"
              control={control}
              label="Kegiatan"
              error={errors.kegiatan}
            />
            {/* jabatan - Text Field */}
            <InputTextFieldForm
              name="jabatan"
              control={control}
              label="Jabatan"
              error={errors.jabatan}
            />

            {/* Tanggal Kegiatan - Date */}
            <InputDateForm
              name="tahun"
              control={control}
              label="Tanggal Kegiatan"
              error={errors.tahun}
              disabled={false}
            />
            <Grid container justifyContent="center" item xs={12}
              paddingTop={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}
              paddingBottom={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}>
              <FullButton variant="contained" type="submit" disabled={isAdding || isEditing}>
                {(isAdding || isEditing) ? "Saving..." : "Save"}
              </FullButton>
            </Grid>
          </ScrollGrid>
        </SquareGrid>
      </Box>
    </Backdrop>
  );
}
